import { ReturnHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  HELP_CENTER_HOW_TO_CONFIGURE_SSO_USING_OIDC,
  SSO_CONFIGURED_TIMEOUT,
} from '@modules/workspaceSettings/constants';
import {
  FETCH_TENANT_SSO_SETTINGS,
  useFetchTenantSSOSettings,
} from '@modules/workspaceSettings/hooks/useFetchTenantSSOSettings';
import { HelpRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useUpdateTenantSsoMutation } from 'generated/graphql';
import { FormEvent, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface ConfigureOIDCSSOConfigModalContentProps {
  onBack: () => void;
}

export const ConfigureOIDCSSOConfigModalContent = ({
  onBack,
}: ConfigureOIDCSSOConfigModalContentProps) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { ssoSettings, isLoading } = useFetchTenantSSOSettings();
  const isSSOEnabled = ssoSettings?.isEnabled;

  const { closeModal } = useMuiModalContext();
  const [clientID, setClientID] = useState('');
  const [issuerURL, setIssuerURL] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const { showSuccessNotification } = useNotificationContext();

  const [updateTenantSSO, { loading }] = useUpdateTenantSsoMutation({
    variables: {
      request: {
        tenantID,
        oidc: {
          clientID: clientID.trim(),
          clientSecret: clientSecret.trim(),
          issuerURL: issuerURL.trim(),
        },
      },
    },
    refetchQueries: [FETCH_TENANT_SSO_SETTINGS],
  });

  const onSubmit = async (event: FormEvent) => {
    try {
      if (isErrorVisible) {
        setIsErrorVisible(false);
      }
      event.preventDefault();
      await updateTenantSSO();
      closeModal();
      showSuccessNotification({
        contentComponent: isSSOEnabled
          ? 'SSO Modified'
          : 'You can test it out by logging out and signing in through SSO.',
        props: {
          isClosable: true,
          title: isSSOEnabled ? '' : 'SSO Configured',
          timeout: SSO_CONFIGURED_TIMEOUT,
        },
      });
    } catch {
      setIsErrorVisible(true);
    }
  };

  const isSubmitDisabled =
    !(clientID && issuerURL && clientSecret) || isLoading;

  return (
    <Box component="form" onSubmit={onSubmit}>
      <DialogContent dividers>
        <Stack gap={3}>
          {isErrorVisible && (
            <Alert severity="error">Error in Configuration of SSO</Alert>
          )}
          <TextField
            required
            value={clientID}
            label="Client ID"
            autoComplete="off"
            placeholder="Enter Client ID"
            onChange={(event) => setClientID(event.target.value)}
          />
          <TextField
            required
            type="password"
            value={clientSecret}
            label="Client Secret"
            autoComplete="new-password"
            placeholder="Enter Client Secret"
            onChange={(event) => setClientSecret(event.target.value)}
          />
          <TextField
            required
            value={issuerURL}
            autoComplete="off"
            label="Issuer URL"
            placeholder="Enter Issuer URL"
            onChange={(event) => setIssuerURL(event.target.value)}
          />
          <Stack direction="row" alignItems="center" spacing={1}>
            <HelpRounded fontSize="small" color="disabled" />
            <Typography variant="caption">
              Need help in configuring SSO using OIDC?
            </Typography>
            <Link
              href={HELP_CENTER_HOW_TO_CONFIGURE_SSO_USING_OIDC}
              target="_blank"
              display="flex"
              alignItems="center"
            >
              <Typography variant="caption">See Documentation</Typography>
            </Link>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ m: 1, mr: 2 }}>
        <Button variant="outlined" onClick={onBack} disabled={loading}>
          Back
        </Button>
        <LoadingButton
          variant="contained"
          type="submit"
          disabled={isSubmitDisabled}
          loading={loading}
          endIcon={<ReturnHotKeyIcon isDisabled={isSubmitDisabled} />}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};
